import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Link2, Copy, Check, Users, MousePointerClick, Trophy,
  Share2, Twitter, Facebook, Linkedin, Mail, Edit2, Loader2,
  Gift, TrendingUp, Star, Award, Crown, Medal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const AffiliateDashboard = () => {
  const { token, user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [updatingSlug, setUpdatingSlug] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchReferralData();
    fetchLeaderboard();
  }, [token]);

  const fetchReferralData = async () => {
    try {
      // First create/get referral
      await fetch(`${API}/api/referrals/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then fetch details
      const response = await fetch(`${API}/api/referrals/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
        setNewSlug(data?.affiliate_slug || '');
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/api/referrals/leaderboard?limit=10`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const affiliateUrl = referralData?.affiliate_slug 
    ? `${baseUrl}/r/${referralData.affiliate_slug}`
    : `${baseUrl}?ref=${referralData?.referral_code}`;

  const copyLink = () => {
    navigator.clipboard.writeText(affiliateUrl);
    setCopied(true);
    toast.success('Affiliate link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    setCheckingSlug(true);
    try {
      const response = await fetch(`${API}/api/referrals/check-slug/${slug}`);
      const data = await response.json();
      setSlugAvailable(data.available || slug === referralData?.affiliate_slug);
    } catch (error) {
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  };

  const updateSlug = async () => {
    if (!newSlug || newSlug.length < 3) {
      toast.error('Slug must be at least 3 characters');
      return;
    }
    
    setUpdatingSlug(true);
    try {
      const response = await fetch(`${API}/api/referrals/slug`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ slug: newSlug })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }
      
      toast.success('Affiliate link updated!');
      setEditDialogOpen(false);
      fetchReferralData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdatingSlug(false);
    }
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Join me on Spinser and win FREE home upgrades! 🏠✨`)}&url=${encodeURIComponent(affiliateUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(affiliateUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(affiliateUrl)}`,
    email: `mailto:?subject=${encodeURIComponent('Win FREE Home Upgrades on Spinser!')}&body=${encodeURIComponent(`Hey! I thought you'd love this - enter to win free home upgrades on Spinser: ${affiliateUrl}`)}`
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'gold': return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'silver': return <Medal className="w-5 h-5 text-gray-400" />;
      default: return <Award className="w-5 h-5 text-amber-600" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'gold': return 'from-yellow-400 to-yellow-600';
      case 'silver': return 'from-gray-300 to-gray-500';
      default: return 'from-amber-500 to-amber-700';
    }
  };

  const getTierBenefits = (tier) => {
    switch (tier) {
      case 'gold': return ['Priority support', '+10 bonus entries per referral', 'Exclusive giveaways access', 'Featured on leaderboard'];
      case 'silver': return ['Priority support', '+7 bonus entries per referral', 'Early access to new giveaways'];
      default: return ['+5 bonus entries per referral', 'Basic support'];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="affiliate-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>
          Affiliate Dashboard
        </h1>
        <p className="text-neutral-500 mt-1">
          Share your link and earn rewards for every signup
        </p>
      </div>

      {/* Affiliate Link Card */}
      <div className="bg-gradient-to-br from-[#FF3B30] to-[#DC2626] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Link2 className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Your Affiliate Link</h2>
            <p className="text-white/80 text-sm">Share this link to earn rewards</p>
          </div>
        </div>
        
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4">
          <div className="flex items-center gap-3">
            <code className="flex-1 text-sm sm:text-base font-mono truncate">
              {affiliateUrl}
            </code>
            <Button
              onClick={copyLink}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 flex-shrink-0"
              data-testid="copy-link-btn"
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => setEditDialogOpen(true)}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            data-testid="customize-link-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" />
            Customize Link
          </Button>
          
          <div className="flex gap-2">
            <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" 
               className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors">
              <Twitter className="w-5 h-5" />
            </a>
            <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer"
               className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors">
              <Facebook className="w-5 h-5" />
            </a>
            <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer"
               className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors">
              <Linkedin className="w-5 h-5" />
            </a>
            <a href={shareLinks.email}
               className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
            <MousePointerClick className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-neutral-900">{referralData?.clicks || 0}</p>
          <p className="text-sm text-neutral-500">Link Clicks</p>
        </div>
        
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
            <Users className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-neutral-900">{referralData?.signups || 0}</p>
          <p className="text-sm text-neutral-500">Signups</p>
        </div>
        
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
            <Gift className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-neutral-900">{referralData?.bonus_entries_earned || 0}</p>
          <p className="text-sm text-neutral-500">Bonus Entries</p>
        </div>
        
        <div className="bg-white rounded-xl border border-neutral-200 p-5">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
            <TrendingUp className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-neutral-900">
            {referralData?.clicks > 0 
              ? Math.round((referralData.signups / referralData.clicks) * 100) 
              : 0}%
          </p>
          <p className="text-sm text-neutral-500">Conversion Rate</p>
        </div>
      </div>

      {/* Tier & Referred Users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Tier */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#FF3B30]" />
            Your Tier
          </h3>
          
          <div className={`bg-gradient-to-r ${getTierColor(referralData?.tier)} rounded-xl p-4 text-white mb-4`}>
            <div className="flex items-center gap-3">
              {getTierIcon(referralData?.tier)}
              <div>
                <p className="text-xl font-bold capitalize">{referralData?.tier || 'Bronze'}</p>
                <p className="text-sm text-white/80">
                  {referralData?.tier === 'gold' ? 'Top Tier!' : 
                   referralData?.tier === 'silver' ? `${20 - (referralData?.signups || 0)} more to Gold` :
                   `${6 - (referralData?.signups || 0)} more to Silver`}
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm font-medium text-neutral-700">Your Benefits:</p>
            {getTierBenefits(referralData?.tier).map((benefit, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-neutral-600">
                <Check className="w-4 h-4 text-green-500" />
                {benefit}
              </div>
            ))}
          </div>
          
          {/* Tier Progress */}
          <div className="mt-4 pt-4 border-t border-neutral-100">
            <div className="flex justify-between text-xs text-neutral-500 mb-2">
              <span>Bronze</span>
              <span>Silver (6)</span>
              <span>Gold (20)</span>
            </div>
            <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div 
                className={`h-full bg-gradient-to-r ${getTierColor(referralData?.tier)} transition-all duration-500`}
                style={{ width: `${Math.min(100, ((referralData?.signups || 0) / 20) * 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Referred Users */}
        <div className="bg-white rounded-xl border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF3B30]" />
            Your Referrals
          </h3>
          
          {referralData?.referred_users_details?.length > 0 ? (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {referralData.referred_users_details.map((user) => (
                <div key={user.user_id || user.email} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg">
                  <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-full flex items-center justify-center text-[#FF3B30] font-medium">
                    {user.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-900 truncate">{user.name}</p>
                    <p className="text-xs text-neutral-500 capitalize">{user.role}</p>
                  </div>
                  <div className="text-xs text-neutral-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No referrals yet</p>
              <p className="text-sm">Share your link to get started!</p>
            </div>
          )}
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Top Referrers
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-neutral-500 border-b border-neutral-100">
                <th className="pb-3 pr-4">Rank</th>
                <th className="pb-3 pr-4">Name</th>
                <th className="pb-3 pr-4">Type</th>
                <th className="pb-3 pr-4">Signups</th>
                <th className="pb-3">Tier</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((ref, i) => (
                <tr key={ref.referrer_id || `rank-${i}`} className={`border-b border-neutral-50 ${ref.referrer_id === user?.user_id ? 'bg-[#FF3B30]/5' : ''}`}>
                  <td className="py-3 pr-4">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </td>
                  <td className="py-3 pr-4 font-medium text-neutral-900">
                    {ref.referrer_name || 'Anonymous'}
                    {ref.referrer_id === user?.user_id && <span className="ml-2 text-xs text-[#FF3B30]">(You)</span>}
                  </td>
                  <td className="py-3 pr-4 capitalize text-neutral-600">{ref.referrer_type}</td>
                  <td className="py-3 pr-4 font-semibold">{ref.signups}</td>
                  <td className="py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize
                      ${ref.tier === 'gold' ? 'bg-yellow-100 text-yellow-700' : 
                        ref.tier === 'silver' ? 'bg-gray-100 text-gray-700' : 
                        'bg-amber-100 text-amber-700'}`}>
                      {getTierIcon(ref.tier)}
                      {ref.tier || 'Bronze'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customize Link Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Customize Your Affiliate Link</DialogTitle>
            <DialogDescription>
              Create a memorable link that's easy to share
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Your Custom Link</Label>
              <div className="flex items-center mt-2">
                <span className="px-3 py-2 bg-neutral-100 border border-r-0 border-neutral-200 rounded-l-lg text-sm text-neutral-500">
                  {baseUrl}/r/
                </span>
                <Input
                  value={newSlug}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                    setNewSlug(value);
                    checkSlugAvailability(value);
                  }}
                  placeholder="yourname"
                  className="rounded-l-none"
                  data-testid="slug-input"
                />
              </div>
              
              {newSlug && newSlug.length >= 3 && (
                <p className={`text-sm mt-2 ${slugAvailable ? 'text-green-600' : 'text-red-600'}`}>
                  {checkingSlug ? 'Checking...' : slugAvailable ? '✓ Available!' : '✗ Already taken'}
                </p>
              )}
              
              <p className="text-xs text-neutral-500 mt-2">
                3-30 characters. Letters, numbers, underscore and hyphen only.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={updateSlug}
              disabled={!slugAvailable || updatingSlug || newSlug.length < 3}
              className="bg-[#FF3B30] hover:bg-[#DC2626]"
              data-testid="save-slug-btn"
            >
              {updatingSlug ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AffiliateDashboard;
